import React from 'react'
import { ReactComponent as IconCheckboxEmpty } from '../icons/CheckboxEmpty.svg'
import { ReactComponent as IconCheckboxFilled } from '../icons/CheckboxFilled.svg'
import { ReactComponent as IconRadioEmpty } from '../icons/RadioEmpty.svg'
import { ReactComponent as IconRadioFilled } from '../icons/RadioFilled.svg'

const components = {
  MuiAutocomplete: {
    styleOverrides: {
      inputRoot: {
        paddingTop: 4,
        paddingBottom: 4,
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    defaultProps: {
      variant: 'contained',
      disableElevation: true,
    },
    styleOverrides: {
      root: ({ theme }) => theme.unstable_sx({
        color: 'text.primary',
        py: 1.5,
        px: 4,
        '&.Mui-disabled': {
          color: 'neutral.light',
          bgcolor: 'neutral.lighter',
        },
      }),
      sizeSmall: ({ theme }) => theme.unstable_sx({
        py: 1,
        px: 3,
        ...theme.typography.button,
      }),
      containedPrimary: ({ theme }) => theme.unstable_sx({
        '&.light': {
          bgcolor: 'primary.light',
          '&:hover': {
            bgcolor: 'primary.main',
          },
        },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => theme.unstable_sx({
        borderRadius: '8px',
        '& .MuiOutlinedInput-notchedOutline': {
          transition: theme.transitions.create(['border-color']),
          borderColor: theme.palette.neutral.input,
        },
        // '&:hover .MuiOutlinedInput-notchedOutline': {
        //   borderColor: theme.palette.neutral.lighter,
        // },
        // '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        //   borderColor: theme.palette.neutral.lighter,
        // },
      }),
      input: {
        height: '20px',
        padding: '10px',
      },
      multiline: ({ theme }) => ({
        borderRadius: '20px',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.neutral.dark,
        },
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => theme.unstable_sx({
        py: 1,
        px: 1.5,
        bgcolor: 'secondary.main',
        ...theme.typography.body1Semibold,
      }),
      arrow: ({ theme }) => theme.unstable_sx({
        color: 'secondary.main',
      }),
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.neutral.dark,
      }),
      light: ({ theme }) => ({
        borderColor: theme.palette.neutral.lighter,
      })
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => theme.unstable_sx({
        '&.drawn': {
          borderRadius: '20px',
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: theme.palette.neutral.dark,
          boxShadow: 'none',
          position: 'relative',

          '&:not(.flat)': {
            mb: '8px',
          },

          '& > * ': {
            position: 'relative',
            zIndex: 3,
          },

          '&:not(.flat):before': {
            content: '""',
            backgroundColor: theme.palette.neutral.lightest,
            position: 'absolute',
            bottom: 0,
            left: -1,
            width: '100%',
            height: 'calc(100% - 50px)',
            zIndex: 2,
            borderRadius: '20px',
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: theme.palette.neutral.dark,
            borderTop: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
          '&:not(.flat):after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: -1,
            width: '100%',
            height: 50,
            borderRadius: '20px',
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: theme.palette.neutral.dark,
            bgcolor: 'primary.lighter',
            zIndex: 1,
          },
          '&.active': {
            bgcolor: 'primary.lightest',
            '&:before': {
              bgcolor: 'primary.lightest',
            },
          },
          '&.inactive': {
            '&:after': {
              bgcolor: 'secondary.lightest',
            },
          },
        },
      }),
    },
  },
  MuiRadio: {
    defaultProps: {
      size: 'small',
      icon: (
        <IconRadioEmpty viewBox="0 0 20 20" />
      ),
      checkedIcon: (
        <IconRadioFilled viewBox="0 0 20 20" />
      ),
    },
    styleOverrides: {
      root: ({ theme }) => theme.unstable_sx({
        padding: 0,
        color: theme.palette.neutral.main,
        'svg': {
          '.icon-radio-empty': {
            fill: theme.palette.neutral.lightest,
          },
          '.icon-radio-filled-bg': {

          },
          '.icon-radio-filled-circle': {
            fill: theme.palette.neutral.lightest,
          },
          '&[font-size="medium"]': {
            width: 30,
            height: 30,
          },
        },
        '&:hover': {
          backgroundColor: 'initial',
        },
        '&.Mui-disabled': {
          color: theme.palette.neutral.dark,
          'svg': {
            '.icon-radio-empty': {
              fill: theme.palette.neutral.main,
            },
            '.icon-radio-filled-bg': {

            },
            '.icon-radio-filled-circle': {
              fill: theme.palette.neutral.main,
            },
          },
        },
      }),
      colorPrimary: ({ theme }) => theme.unstable_sx({
        '&:not(.Mui-disabled)': {
          'svg': {
            color: theme.palette.primary.main,
          },
        },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      colorSuccess: ({ theme }) => ({
        backgroundColor: '#56A06F',
        ...theme.typography.button,
        borderRadius: '8px',
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      icon: (
        <IconCheckboxEmpty viewBox="0 0 20 20" />
      ),
      checkedIcon: (
        <IconCheckboxFilled viewBox="0 0 20 20" />
      ),
    },
    styleOverrides: {
      root: ({ theme }) => theme.unstable_sx({
        width: 20,
        height: 20,
        borderRadius: 5,
        borderWidth: 2,
        padding: 0,
        color: theme.palette.neutral.main,
        'svg': {
          '.icon-checkbox-empty': {
            fill: theme.palette.neutral.lightest,
          },
          '.icon-checkbox-filled-bg': {

          },
          '.icon-checkbox-filled-check': {
            stroke: theme.palette.neutral.lightest,
          },
        },
        '&:hover': {
          backgroundColor: 'initial',
        },
        '&.Mui-disabled': {
          color: theme.palette.neutral.dark,
          'svg': {
            '.icon-checkbox-empty': {
              fill: theme.palette.neutral.main,
            },
            '.icon-checkbox-filled-bg': {

            },
            '.icon-checkbox-filled-check': {
              stroke: theme.palette.neutral.main,
            },
          },
        },
      }),
      colorPrimary: ({ theme }) => theme.unstable_sx({
        '&:not(.Mui-disabled)': {
          'svg': {
            color: theme.palette.primary.main,
          },
        },
      }),
      // colorSecondary: ({ theme }) => theme.unstable_sx({
      //   '&:not(.Mui-disabled)': {
      //     'svg': {
      //       color: theme.palette.secondary.main,
      //     },
      //   },
      // }),
    },
    // styleOverrides: {
    //   root: {
    //     width: 20,
    //     height: 20,
    //     borderRadius: 5,
    //     borderWidth: 2,
    //     '&.Mui-checked': {
    //       color: '#F2B943',
    //       borderColor: '#F2B943',
    //       backgroundColor: '#FAE4B7',
    //     },
    //     '&.MuiCheckbox-colorPrimary.Mui-disabled': {
    //       borderColor: '#393939',
    //       backgroundColor: '#6F787F',
    //       '&.Mui-checked': {
    //         color: '#393939',
    //       },
    //     },
    //   },
    //   colorPrimary: {
    //     '& .MuiSvgIcon-root': {
    //       width: 12,
    //       height: 10,
    //     },
    //     '& .MuiSvgIcon-root path': {
    //       fill: '#F2B943',
    //     },
    //     '&:not(.Mui-checked)': {
    //       borderColor: '#C8D1E0',
    //       color: '#C8D1E0',
    //       backgroundColor: '#FFFFFF',
    //     },
    //   },
    // },
  },
  MuiLink: {
    defaultProps: {
      color: 'secondary',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.link,
        transition: theme.transitions.create(['color']),
        '&:hover': {
          color: theme.palette.secondary.dark,
        },
        '&.primary': {
          '&:hover': {
            color: theme.palette.primary.dark,
          },
        },
      }),
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: ({ theme }) => ({
        width: 55,
        height: 25,
        padding: 0,
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }),
      switchBase: ({ theme }) => ({
        padding: 2,
        '&.Mui-checked': {
          transform: 'translateX(30px)',
        },
        '&.Mui-checked + .MuiSwitch-track': {
          backgroundColor: theme.palette.primary.main,
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.neutral.main,
          },
          '& .MuiSwitch-thumb': {
            backgroundColor: theme.palette.neutral.light,
          },
        },
      }),
      thumb: {
        width: 21,
        height: 21,
        boxShadow: 'none',
        backgroundColor: '#FFFFFF',
      },
      track: ({ theme }) => ({
        borderRadius: 25 / 2,
        backgroundColor: '#B3B8BC',
        opacity: '1 !important',
        height: 25,
      }),
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        width: '100%',
        gap: '10px',
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        margin: 0,
        display: 'inline-flex',
        gridGap: 10,
        gridAutoFlow: 'column',
        justifyContent: 'flex-start',
      },
      labelPlacementStart: {
        '& .MuiFormControlLabel-label': {
          order: -1,
        },
      },
      labelPlacementTop: {
        gridAutoFlow: 'row',
        gridGap: 5,
        '& .MuiFormControlLabel-label': {
          order: -1,
        },
      },
      labelPlacementBottom: {
        gridAutoFlow: 'row',
        gridGap: 5,
      },
    },
  },
  MuiFormGroup: {
    styleOverrides: {
      root: {
        display: 'inline-flex',
        gap: 30,
        '&[role="radiogroup"]': {
          gap: 20,
        },
      },
      row: {
        gridAutoFlow: 'column',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
          fontSize: 24,
          lineHeight: 1.2,
          fontWeight: 600,
          paddingTop: '20px',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        minWidth: '400px',
        paddingBottom: '20px',
      },
    },
  },
  // MuiFormHelperText: {
  //   styleOverrides: {
  //     root: {
  //       margin: 0,
  //       fontWeight: 600,
  //     },
  //   },
  // },
}

export default components